import type { TastingPhoto } from '@recipes/contracts';
import { cn } from '@recipes/ui';
import { Check, ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Which photo of the dish becomes the picture of the recipe made from it. The first one is
 * chosen from the start – the dish as it was served is the best picture such a recipe can begin
 * with – and "no picture" is one tap away. The server copies the photo when the recipe is saved.
 */
export function DishPhotoChoice({
  photos,
  value,
  onChange,
}: {
  photos: TastingPhoto[];
  value: string | null;
  onChange: (assetId: string | null) => void;
}) {
  const { t } = useTranslation();
  if (photos.length === 0) return null;
  const tile =
    'relative size-24 shrink-0 overflow-hidden rounded-lg border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <fieldset className="rounded-lg border bg-card p-4" data-testid="dish-photo-choice">
      <legend className="px-1 text-sm font-semibold">{t('tasting.suggest.picture.title')}</legend>
      <p className="mb-3 text-sm text-muted-foreground">
        {t('tasting.suggest.picture.description')}
      </p>
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {photos.map((photo, index) => {
          const chosen = value === photo.assetId;
          return (
            <button
              key={photo.assetId}
              type="button"
              role="radio"
              aria-checked={chosen}
              aria-label={
                photo.altText ?? t('tasting.suggest.picture.photoAlt', { index: index + 1 })
              }
              onClick={() => onChange(photo.assetId)}
              className={cn(tile, chosen ? 'border-primary' : 'border-transparent')}
              data-testid="dish-photo-option"
            >
              <img
                src={photo.thumbnailUrl ?? photo.url ?? ''}
                alt=""
                className="size-full object-cover"
              />
              {chosen && (
                <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                  <Check className="size-4" aria-hidden />
                </span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          role="radio"
          aria-checked={value === null}
          onClick={() => onChange(null)}
          className={cn(
            tile,
            'flex flex-col items-center justify-center gap-1 bg-muted/50 text-xs text-muted-foreground',
            value === null ? 'border-primary' : 'border-transparent',
          )}
          data-testid="dish-photo-none"
        >
          <ImageOff className="size-5" aria-hidden />
          {t('tasting.suggest.picture.none')}
        </button>
      </div>
    </fieldset>
  );
}
